import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { combatsAPI } from '../services/api';
import { 
  Swords, 
  Users, 
  TrendingUp, 
  Shield, 
  ArrowRight,
  Clock,
  DollarSign,
  Zap
} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import Coin from '../components/Coin';

const Home = () => {
  // Récupérer les combats à venir
  const { data: combatsData, isLoading } = useQuery(
    ['combats', 'upcoming'],
    () => combatsAPI.getAll({ status: 'upcoming', limit: 3 }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Auth state
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const features = [
    {
      icon: Swords,
      title: 'Combats Épiques',
      description: 'Assistez aux meilleurs combats de coqs d\'Haïti avec des paris en temps réel.'
    },
    {
      icon: Shield,
      title: 'Paiements Sécurisés',
      description: 'Parier en toute sécurité avec MonCash, la solution de paiement mobile haïtienne.'
    },
    {
      icon: TrendingUp,
      title: 'Cotes Avantageuses',
      description: 'Profitez des meilleures cotes du marché pour maximiser vos gains.'
    },
    {
      icon: Users,
      title: 'Communauté Active',
      description: 'Rejoignez une communauté passionnée de parieurs et d\'amateurs de combats.'
    }
  ];

  const stats = [
    { label: 'Combats', value: '50+', icon: Swords },
    { label: 'Utilisateurs', value: '1000+', icon: Users },
    { label: 'Pari Total', value: <span className="inline-flex items-center"><Coin amount={'500K+'} symbol="HTG" /></span>, icon: DollarSign },
    { label: 'Temps Réel', value: '24/7', icon: Clock }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur">
                <Swords className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-shadow-lg">
              KokBatay
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100 max-w-3xl mx-auto">
              La plateforme de paris moderne pour les combats de coqs en Haïti
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/combats"
                className="btn bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
              >
                Voir les Combats
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to={isAuthenticated ? "/combats" : "/register"}
                className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-3 text-lg font-semibold"
              >
                Commencer à Parier
              </Link>
            </div>
          </div>
        </div>
        
        {/* Vague décorative */}
        {/* <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
              fill="currentColor"
              className="text-gray-50"
            />
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              fill="currentColor"
              className="text-gray-50"
            />
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill="currentColor"
              className="text-gray-50"
            />
          </svg>
        </div> */}
      </section>

      {/* Statistiques */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir KokBatay ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une expérience de paris moderne, sécurisée et adaptée à la culture haïtienne
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="card p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-primary-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Combats à venir */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Prochains Combats
              </h2>
              <p className="text-gray-600">
                Ne manquez pas les combats les plus attendus
              </p>
            </div>
            <Link
              to="/combats"
              className="btn-primary"
            >
              Voir Tous
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {combatsData?.combats?.slice(0, 3).map((combat) => (
                <div key={combat.id} className="card p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {combat.title}
                    </h3>
                    <span className="badge-warning">
                      {combat.status === 'upcoming' ? 'À venir' : combat.status}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2"></div>
                      <div className="text-sm font-medium text-gray-900">{combat.cock1_name}</div>
                      <div className="text-lg font-bold text-primary-600">{combat.cock1_odds}</div>
                    </div>
                    
                    <div className="text-center text-gray-500">
                      <div className="text-2xl font-bold">VS</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2"></div>
                      <div className="text-sm font-medium text-gray-900">{combat.cock2_name}</div>
                      <div className="text-lg font-bold text-primary-600">{combat.cock2_odds}</div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                    <span>Total paris: {combat.total_bets_count || 0}</span>
                    <span>Montant: <Coin amount={combat.total_bets || 0} symbol="HTG" /></span>
                  </div>
                  
                  <Link
                    to={`/combats/${combat.id}`}
                    className="btn-primary w-full"
                  >
                    Voir Détails
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Prêt à Commencer ?
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            Rejoignez des milliers de parieurs et vivez l'excitation des combats de coqs haïtiens
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
            >
              <Zap className="mr-2 w-5 h-5" />
              Créer un Compte
            </Link>
            <Link
              to="/combats"
              className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-3 text-lg font-semibold"
            >
              Voir les Combats
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 